import React from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { downloadCsv } from '../helpers';
import { CsvButton, getPlatformIcon, WhyThisMatters } from '../components/Shared.jsx';

const scoreColor = (s) => s >= 80 ? T.success : s >= 55 ? T.warning : T.error;

export function HealthCheck({ data }) {
  const { overallScore, platformScores = {}, issuesFound = [], actionPlan = [], strengthsIdentified = [], summary } = data;

  const handleCsvExport = () => downloadCsv(
    'health-check.csv',
    ['Severity', 'Issue', 'Platforms', 'Fix'],
    issuesFound.map((i) => [i.severity, i.issue, (i.platform || []).join(', '), i.fix])
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <CsvButton onClick={handleCsvExport} />
      </Box>

      {/* Overall Score Banner */}
      <Box sx={{ mb: 2.5, p: 2.5, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ flexShrink: 0, width: 80, height: 80, borderRadius: '50%', border: `4px solid ${scoreColor(overallScore ?? 0)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: scoreColor(overallScore ?? 0), lineHeight: 1 }}>{overallScore ?? '—'}</Typography>
            <Typography sx={{ fontSize: 9, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>/100</Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.text, mb: 0.5 }}>Overall Health Score</Typography>
          <Typography sx={{ fontSize: 12, color: T.textSecondary, mb: 1.5 }}>
            {(overallScore ?? 0) >= 80
              ? 'Excellent — your podcast is well-optimized across all platforms.'
              : (overallScore ?? 0) >= 55
                ? 'Good — a few improvements can boost your reach.'
                : 'Needs Work — key issues detected that may limit discoverability.'}
          </Typography>
          <Box sx={{ height: 6, borderRadius: 3, background: T.hover, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${overallScore ?? 0}%`, background: scoreColor(overallScore ?? 0), borderRadius: 3 }} />
          </Box>
        </Box>
      </Box>

      {/* Per-Platform Score Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 3 }}>
        {[
          { key: 'apple',   label: 'Apple Podcasts', icon: getPlatformIcon('apple') },
          { key: 'spotify', label: 'Spotify',         icon: getPlatformIcon('spotify') },
          { key: 'google',  label: 'Google Search', icon: getPlatformIcon('google') },
        ].map(({ key, label, icon }) => {
          const score      = platformScores[key];
          const platIssues = issuesFound.filter((iss) => (iss.platform || []).some((p) => p.toLowerCase().includes(key === 'google' ? 'google' : key)));
          const highCount  = platIssues.filter((i) => i.severity === 'HIGH').length;
          return (
            <Box key={key} sx={{ p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <Typography sx={{ fontSize: 16, lineHeight: 1 }}>{icon}</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, lineHeight: 1.2 }}>{label}</Typography>
              </Box>
              <Typography sx={{ fontSize: 34, fontWeight: 800, color: scoreColor(score ?? 0), lineHeight: 1, mb: 0.75 }}>{score ?? '—'}</Typography>
              <Box sx={{ height: 5, borderRadius: 2.5, background: T.surface3, overflow: 'hidden', mb: 1 }}>
                <Box sx={{ height: '100%', width: `${score ?? 0}%`, background: scoreColor(score ?? 0), borderRadius: 2.5 }} />
              </Box>
              {platIssues.length > 0 ? (
                <Typography sx={{ fontSize: 11, color: highCount > 0 ? T.error : T.warning }}>
                  {highCount > 0 ? `${highCount} high severity issue${highCount > 1 ? 's' : ''}` : `${platIssues.length} issue${platIssues.length > 1 ? 's' : ''}`}
                </Typography>
              ) : score != null ? (
                <Typography sx={{ fontSize: 11, color: T.success }}>✓ No issues found</Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>

      {issuesFound.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.error, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Issues Found</Typography>
          {issuesFound.map((iss, i) => {
            const isHigh = iss.severity === 'HIGH';
            const isMed  = iss.severity === 'MED';
            return (
              <Box key={i} sx={{ mb: 1, p: 1.5, background: isHigh ? 'rgba(239,68,68,0.08)' : isMed ? 'rgba(234,179,8,0.08)' : T.surface, border: `1px solid ${isHigh ? 'rgba(239,68,68,0.3)' : isMed ? 'rgba(234,179,8,0.3)' : T.border}`, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Box sx={{ px: 1, py: 0.25, background: isHigh ? 'rgba(239,68,68,0.2)' : isMed ? 'rgba(234,179,8,0.2)' : T.hover, color: isHigh ? T.error : isMed ? T.warning : T.textSecondary, borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{iss.severity}</Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{iss.issue}</Typography>
                  {(iss.platform || []).map((p, j) => (
                    <Box key={j} sx={{ px: 1, py: 0.25, background: T.surface3, color: T.textSecondary, borderRadius: 4, fontSize: 11, border: `1px solid ${T.border}` }}>{p}</Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: 12, color: T.textSecondary }}>Fix: {iss.fix}</Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {actionPlan.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Action Plan</Typography>
          {actionPlan.map((item, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1, display: 'flex', gap: 1.5 }}>
              <Box sx={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: T.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, mt: 0.25 }}>{item.priority}</Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{item.action}</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
                  <Typography sx={{ fontSize: 11, color: T.textSecondary }}>Impact: {item.impact}</Typography>
                  <Typography sx={{ fontSize: 11, color: T.textSecondary }}>Time: {item.timeToComplete}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {strengthsIdentified.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.success, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>✓ Strengths</Typography>
          {strengthsIdentified.map((s, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: 'rgba(34,197,94,0.07)', border: `1px solid rgba(34,197,94,0.25)`, borderRadius: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.strength}</Typography>
              <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.25 }}>{s.value} · {s.platform.join(', ')}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {summary && (
        <Box sx={{ mb: 3, p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
          <Typography sx={{ fontSize: 13, color: T.textSecondary }}>{summary}</Typography>
        </Box>
      )}

      <WhyThisMatters text="A healthy RSS feed ensures proper syndication and discoverability across all platforms." citations={data.expertCitations} />
    </Box>
  );
}
